// Per-feature spec client hook. Owns fetch / generate / save against
// /api/sessions/{id}/feature-specs.

import { useCallback, useEffect, useState } from 'react';
import type { FeatureSpec, FeatureSpecsPayload } from './sessionTypes';

const EMPTY: FeatureSpecsPayload = { specs: {} };

export interface UseFeatureSpecsReturn {
  payload: FeatureSpecsPayload;
  loading: boolean;
  generating: Record<string, boolean>;   // per-feature spinner
  saving: Record<string, boolean>;
  generatingAll: boolean;
  error: string | null;
  refresh: () => Promise<void>;
  generate: (featureId: string, opts?: { force?: boolean }) => Promise<FeatureSpec | null>;
  save: (featureId: string, markdown: string) => Promise<FeatureSpec | null>;
  generateAll: (
    featureIds: string[],
    onProgress?: (done: number, total: number) => void,
  ) => Promise<void>;
}

export function useFeatureSpecs(sessionId: string | null): UseFeatureSpecsReturn {
  const [payload, setPayload] = useState<FeatureSpecsPayload>(EMPTY);
  const [loading, setLoading] = useState(false);
  const [generating, setGenerating] = useState<Record<string, boolean>>({});
  const [saving, setSaving] = useState<Record<string, boolean>>({});
  const [generatingAll, setGeneratingAll] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const refresh = useCallback(async () => {
    if (!sessionId) return;
    setLoading(true);
    setError(null);
    try {
      const res = await fetch(`/api/sessions/${sessionId}/feature-specs`);
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const data = await res.json();
      setPayload({ specs: data?.specs || {} });
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load specs');
    } finally {
      setLoading(false);
    }
  }, [sessionId]);

  const setBusy = (id: string, busy: boolean, key: 'generating' | 'saving') => {
    const setter = key === 'generating' ? setGenerating : setSaving;
    setter((prev) => {
      const next = { ...prev };
      if (busy) next[id] = true; else delete next[id];
      return next;
    });
  };

  const generate = useCallback(async (featureId: string, opts: { force?: boolean } = {}) => {
    if (!sessionId) return null;
    setBusy(featureId, true, 'generating');
    setError(null);
    try {
      const res = await fetch(`/api/sessions/${sessionId}/feature-specs/${encodeURIComponent(featureId)}/generate`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ force: !!opts.force }),
      });
      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        if (res.status === 409 && data?.userEdited) {
          throw new Error('Spec has manual edits — pass force=true to overwrite.');
        }
        throw new Error(data?.error || `HTTP ${res.status}`);
      }
      const data = await res.json();
      const spec: FeatureSpec = data.spec;
      setPayload((prev) => ({ specs: { ...prev.specs, [featureId]: spec } }));
      return spec;
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Generation failed');
      return null;
    } finally {
      setBusy(featureId, false, 'generating');
    }
  }, [sessionId]);

  const save = useCallback(async (featureId: string, markdown: string) => {
    if (!sessionId) return null;
    setBusy(featureId, true, 'saving');
    setError(null);
    try {
      const res = await fetch(`/api/sessions/${sessionId}/feature-specs/${encodeURIComponent(featureId)}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ markdown }),
      });
      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        throw new Error(data?.error || `HTTP ${res.status}`);
      }
      const data = await res.json();
      const spec: FeatureSpec = data.spec;
      setPayload((prev) => ({ specs: { ...prev.specs, [featureId]: spec } }));
      return spec;
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Save failed');
      return null;
    } finally {
      setBusy(featureId, false, 'saving');
    }
  }, [sessionId]);

  const generateAll = useCallback(async (
    featureIds: string[],
    onProgress?: (done: number, total: number) => void,
  ) => {
    if (!sessionId || featureIds.length === 0) return;
    setGeneratingAll(true);
    setError(null);
    try {
      let done = 0;
      for (const id of featureIds) {
        await generate(id, { force: false });
        done += 1;
        onProgress?.(done, featureIds.length);
      }
    } finally {
      setGeneratingAll(false);
    }
  }, [sessionId, generate]);

  useEffect(() => { refresh(); }, [refresh]);

  return { payload, loading, generating, saving, generatingAll, error, refresh, generate, save, generateAll };
}
